import { useState, type ReactNode } from 'react';
import { Menu, X, Search, MapPin, Dumbbell, Waves, Trophy } from 'lucide-react';
import { Logo, Button, cn } from '@techsofit/ui';
import { SIGNIN_URL, START_URL, PRICING_URL, openDemo } from '../config';

export function Container({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8', className)}>{children}</div>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="text-eyebrow uppercase text-muted">{children}</p>;
}

const NAV_LINKS = [
  { href: '/#features', label: 'Platform' },
  { href: '/#industries', label: 'Industries' },
  { href: PRICING_URL, label: 'Pricing' },
];

export function NavBar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-line/60 bg-canvas/80 backdrop-blur-md">
      <Container className="flex h-16 items-center justify-between">
        <a href="/" aria-label="TechSoFit home">
          <Logo onDark />
        </a>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="rounded-pill px-3.5 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-card hover:text-ink">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <a href={SIGNIN_URL} className="rounded-pill px-3.5 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-card hover:text-ink">
            Log in
          </a>
          <Button size="sm" onClick={() => { window.location.href = START_URL; }}>
            Get started
          </Button>
        </div>

        <button className="md:hidden" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen((v) => !v)}>
          {open ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </Container>

      {open && (
        <Container className="flex flex-col gap-1 pb-4 md:hidden">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="rounded-control px-3 py-2.5 text-sm font-medium text-ink-soft hover:bg-card">
              {l.label}
            </a>
          ))}
          <a href={SIGNIN_URL} className="rounded-control px-3 py-2.5 text-sm font-medium text-ink-soft hover:bg-card">
            Log in
          </a>
          <Button className="mt-2" block onClick={() => { setOpen(false); window.location.href = START_URL; }}>
            Get started
          </Button>
        </Container>
      )}
    </header>
  );
}

/** Hero visual: a compact "Discover" preview: search + activities + a couple of
 * curated listings. Sells the consumer/directory promise (we lead with Explore). */
export function DiscoverPanel() {
  const chips = ['Gym', 'Swimming', 'Tennis', 'Yoga', 'Boxing', 'Padel', 'Running', 'Climbing'];
  const listings = [
    { name: 'Iron Peak Fitness', kind: 'Gym · Avondale', icon: <Dumbbell className="size-4" /> },
    { name: 'City Aquatic Centre', kind: 'Swimming · CBD', icon: <Waves className="size-4" /> },
    { name: 'Eastside Courts', kind: 'Tennis & padel · Greendale', icon: <Trophy className="size-4" /> },
  ];
  return (
    <div className="rounded-card bg-card p-3 shadow-card sm:p-4">
      <div className="flex items-center gap-2 rounded-pill bg-surface px-3 py-2.5 text-sm text-muted">
        <Search className="size-4 shrink-0" />
        <span className="flex-1">What do you feel like?</span>
        <span className="hidden items-center gap-1 text-xs sm:inline-flex">
          <MapPin className="size-3.5" /> Your area
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <span key={c} className="rounded-pill bg-surface px-3 py-1 text-xs font-medium text-ink-soft">
            {c}
          </span>
        ))}
      </div>

      <div className="mt-3 space-y-2">
        {listings.map((l) => (
          <div key={l.name} className="flex items-center gap-3 rounded-control bg-elevated p-2.5">
            <span className="inline-flex size-9 items-center justify-center rounded-full bg-accent-soft text-ink">
              {l.icon}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{l.name}</p>
              <p className="truncate text-xs text-muted">{l.kind}</p>
            </div>
            <span className="rounded-pill bg-accent px-2.5 py-1 text-xs font-medium text-accent-ink">View</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="bg-[#184a43] text-white/75">
      <Container className="flex flex-col gap-8 py-12 md:flex-row md:items-start md:justify-between">
        <div className="max-w-xs">
          <Logo onDark />
          <p className="mt-3 text-sm text-white/60">
            The technology behind thriving fitness and wellness businesses. By Techsolutely.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-8">
          <FooterCol
            title="Platform"
            links={[
              { label: 'Features', href: '/#features' },
              { label: 'Industries', href: '/#industries' },
              { label: 'Pricing', href: PRICING_URL },
            ]}
          />
          <FooterCol
            title="Get started"
            links={[
              { label: 'Start free trial', href: START_URL },
              { label: 'Book a demo', onClick: openDemo },
              { label: 'Log in', href: SIGNIN_URL },
            ]}
          />
        </div>
      </Container>
      <Container className="flex flex-col items-center justify-between gap-2 border-t border-white/15 py-6 text-xs text-white/55 sm:flex-row">
        <p>
          © 2026{' '}
          <a
            href="https://www.techsolutely.co.zw"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-white hover:underline"
          >
            Techsolutely Innovations
          </a>
          . All rights reserved.
        </p>
        <p>Built in Zimbabwe</p>
      </Container>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { label: string; href?: string; onClick?: () => void }[] }) {
  const cls = 'text-sm text-white/75 transition-colors hover:text-white';
  return (
    <div>
      <p className="text-eyebrow uppercase text-white/50">{title}</p>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            {l.onClick ? (
              <button type="button" onClick={l.onClick} className={cls}>
                {l.label}
              </button>
            ) : (
              <a href={l.href} className={cls}>
                {l.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
